import {useTranslation} from 'react-i18next';

import {Box, Typography} from '@mui/material';

import {profileComparisonSectionCardSx} from './profileComparisonSectionStyles';
import type {ProfileComparisonGapBreakdown as ProfileComparisonGapBreakdownData} from '../../types/ProfileComparisonTypes';
import {capGapBreakdownItems} from '../../utils/ProfileComparison/capGapBreakdownItems';

export interface Props {
  gapBreakdown: ProfileComparisonGapBreakdownData;
}

interface GapColumnProps {
  title: string;
  items: string[];
  testId: string;
}

const GapColumn = ({title, items, testId}: GapColumnProps) => {
  const {t} = useTranslation('translation', {
    keyPrefix: 'workforceDepartmentHealthPage.profileComparison.result.gapBreakdown',
  });
  const {visibleItems, remainingCount} = capGapBreakdownItems(items);

  return (
    <Box sx={profileComparisonSectionCardSx} data-testid={testId}>
      <Typography sx={{fontSize: '12px', fontWeight: 600, mb: 1.25}}>{title}</Typography>
      {visibleItems.length === 0 ? (
        <Typography sx={{fontSize: '12px', color: 'text.secondary'}}>{t('empty')}</Typography>
      ) : (
        <Box component='ul' sx={{m: 0, pl: 2.25}}>
          {visibleItems.map((item) => (
            <Typography key={item} component='li' sx={{fontSize: '12px', color: 'text.primary', mb: 0.5}}>
              {item}
            </Typography>
          ))}
        </Box>
      )}
      {remainingCount > 0 && (
        <Typography sx={{fontSize: '11px', color: 'text.secondary', mt: 0.75}} data-testid={`${testId}-overflow`}>
          {t('overflow', {count: remainingCount})}
        </Typography>
      )}
    </Box>
  );
};

export const ProfileComparisonGapBreakdown = ({gapBreakdown}: Props) => {
  const {t} = useTranslation('translation', {
    keyPrefix: 'workforceDepartmentHealthPage.profileComparison.result.gapBreakdown',
  });

  return (
    <Box data-testid='profile-comparison-gap-breakdown'>
      <Typography sx={{fontSize: '13px', fontWeight: 600, mb: 1.25}}>{t('title')}</Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {xs: '1fr', md: 'repeat(3, 1fr)'},
          gap: 1.5,
          mb: 2,
        }}
      >
        <GapColumn title={t('onlyProfileA')} items={gapBreakdown.onlyProfileA} testId='gap-column-only-a' />
        <GapColumn title={t('onlyProfileB')} items={gapBreakdown.onlyProfileB} testId='gap-column-only-b' />
        <GapColumn title={t('shared')} items={gapBreakdown.shared} testId='gap-column-shared' />
      </Box>
    </Box>
  );
};
